import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import clsx from 'clsx';

type VideoPlayerProps = {
  src: string;
  onClick?: () => void;
  className?: string;
  showHoverPlay?: boolean;
};

export default function VideoPlayer({
  src,
  onClick,
  className,
  showHoverPlay = true,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          videoRef.current?.play().catch(() => {
            // Autoplay may be blocked until the user interacts with the page.
          });
        } else {
          videoRef.current?.pause();
        }
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const handleVideoLoadedMetadata = () => {
    const video = videoRef.current;
    if (video && video.videoWidth && video.videoHeight) {
      const ratio = video.videoWidth / video.videoHeight;
      setAspectRatio(ratio);
    }
  };

  const handleVideoLoaded = () => {
    setIsLoading(false);
  };

  return (
    <button
      ref={containerRef}
      type="button"
      onClick={onClick}
      style={aspectRatio ? { aspectRatio: aspectRatio.toString() } : undefined}
      className={clsx(
        `group relative overflow-hidden rounded-lg shadow-lg`,
        `border-2 border-gray-200 bg-gray-200`,
        `${showHoverPlay ? 'hover:shadow-xl transition-shadow' : ''}`,
        !aspectRatio && 'aspect-video', // Fallback to 16:9 if aspect ratio not yet determined
        className,
      )}>
      {/* Loading skeleton */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-gray-300/50" />
        </div>
      )}

      {shouldLoad && (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedMetadata={handleVideoLoadedMetadata}
          onLoadedData={handleVideoLoaded}
          onCanPlay={handleVideoLoaded}
          className={clsx(
            'absolute inset-0 h-full w-full object-cover transition',
            isLoading && 'opacity-0',
            !isLoading && 'opacity-100',
            showHoverPlay && 'group-hover:scale-101',
          )}>
          Your browser does not support the video tag.
        </video>
      )}

      {showHoverPlay && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/85 shadow-lg ring-1 ring-black/5 transition transform group-hover:scale-105 group-hover:bg-white">
            <Play className="h-7 w-7 text-black" />
          </div>
        </div>
      )}
    </button>
  );
}
